import { useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../lib/auth";
import { parseOperatingMode } from "../lib/types/enums";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { AccountMenu } from "./AccountMenu";
import { Shell, sectionOf, type ShellLink } from "@ui/react";
import {
  ChatIcon,
  InboxIcon,
  MoneyIcon,
  SearchIcon,
  SettingsIcon,
  ToolsIcon,
  UserIcon,
} from "@ui/react/icons";

/**
 * `OperatingMode` is mutually exclusive — a marketplace contractor has no
 * bookings and a bookable one has no job feed. Rendering both sets would give
 * every contractor a permanently empty half of the navigation, so the shell
 * shows only the side they actually work.
 *
 * FIVE destinations either way, and every one of them is in the bottom tab bar
 * below 860px. There is no `primary` flag any more: the old shell filtered this
 * list for the phone bar and "Booking setup" — which has no `primary: true` —
 * silently fell off it, so a bookable contractor on a phone could reach
 * /settings/* only by typing the URL, and leave only with the back button.
 */
function navLinks(mode: "marketplace" | "bookable"): ShellLink[] {
  const shared: ShellLink[] = [
    { href: "/messages", label: "Messages", icon: <ChatIcon />, section: "messages" },
    { href: "/earnings", label: "Earnings", icon: <MoneyIcon />, section: "earnings" },
    { href: "/profile", label: "Profile", icon: <UserIcon />, section: "profile" },
  ];

  if (mode === "bookable") {
    return [
      { href: "/bookings", label: "Bookings", icon: <InboxIcon />, section: "bookings" },
      ...shared,
      {
        href: "/settings/booking",
        label: "Booking setup",
        icon: <SettingsIcon />,
        // Every /settings/* route — services, add-ons, availability, rules —
        // lights this one tab.
        section: "settings",
      },
    ];
  }

  return [
    { href: "/jobs", label: "Find jobs", icon: <SearchIcon />, section: "jobs" },
    { href: "/active", label: "Active work", icon: <ToolsIcon />, section: "active" },
    ...shared,
  ];
}

export function AppShell({ children }: { children: ReactNode }) {
  const { contractor } = useAuth();
  const location = useLocation();
  const firstRender = useRef(true);

  const mode = parseOperatingMode(contractor?.operatingMode);
  const links = navLinks(mode);
  const home = mode === "bookable" ? "/bookings" : "/jobs";

  // On a client-routed app nothing moves focus when the view changes, so a
  // keyboard or screen-reader user is left wherever the old page put them.
  //
  // The target is the PAGE HEAD's h1 — the only heading on the document, and
  // the one that actually changed. It is found by query rather than by a ref
  // threaded through every screen: the shell does not render the heading and
  // must not have to know which of a screen's branches (loaded, loading, error)
  // is on show. `#main` is the fallback for the routes that are still fetching
  // and have no heading yet.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const heading = document.querySelector<HTMLElement>("#main h1");
    (heading ?? document.getElementById("main"))?.focus();
  }, [location.pathname]);

  return (
    <Shell
      brandHref={home}
      links={links}
      currentSection={sectionOf(location.pathname)}
      // The one screen that FITS the viewport instead of flowing down the page:
      // the thread's composer has to stay put while the log scrolls. Without
      // this the chat pane sized itself by arithmetic, and on a landscape phone
      // it clipped the composer clean away — see `.chat` in contractor.css.
      fit={location.pathname.startsWith("/messages")}
      account={
        <>
          <NotificationBell />
          <AccountMenu />
        </>
      }
      renderBrand={({ href, className, children: brand }) => (
        <Link to={href} className={className}>
          {brand}
        </Link>
      )}
      renderLink={({ href, current, children: label }) => (
        <Link to={href} aria-current={current ? "page" : undefined}>
          {label}
        </Link>
      )}
    >
      {children}
    </Shell>
  );
}
